import { FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { APP_NAME, APP_TAGLINE } from "../constants/brand";
import { login, register, resendVerificationEmail } from "../services/api";
import { useAuth } from "../services/auth-context";
import {
  getRememberedCredentials,
  setRememberedCredentials,
  clearRememberedCredentials
} from "../services/session";

export function AuthPage() {
  const { saveSession } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const remembered = getRememberedCredentials();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [accountType, setAccountType] = useState<"business" | "consultant">(
    searchParams.get("consultant") === "true" ? "consultant" : "business"
  );
  const isConsultantMode = accountType === "consultant";
  const [email, setEmail] = useState(remembered?.email ?? "");
  const [password, setPassword] = useState(remembered?.password ?? "");
  const [rememberMe, setRememberMe] = useState(Boolean(remembered));
  const [workspaceName, setWorkspaceName] = useState("Studio Solleciti");
  const [fullName, setFullName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (mode === "register" && !acceptedTerms) {
      setError("Devi accettare le Condizioni d'uso e l'Informativa Privacy per registrarti.");
      return;
    }

    setLoading(true);

    try {
      if (mode === "login") {
        const session = await login(email, password);
        if (rememberMe) {
          setRememberedCredentials({ email, password });
        } else {
          clearRememberedCredentials();
        }
        saveSession(session);
        // Do not navigate explicitly: AuthEntryRoute re-evaluates once billing
        // status loads and redirects to /dashboard or /plans accordingly.
      } else {
        const result = await register({
          email,
          password,
          workspace_name: isConsultantMode ? "" : workspaceName,
          full_name: fullName || undefined,
          is_consultant: isConsultantMode || undefined,
          consultant_invite_token: inviteToken || undefined,
        });

        setPendingVerificationEmail(result.email);
        setNotice(result.message);
        setMode("login");
        setPassword("");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Errore autenticazione";
      setError(message);
      if (mode === "login" && message.toLowerCase().includes("email non verificata")) {
        setPendingVerificationEmail(email);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    if (!pendingVerificationEmail) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await resendVerificationEmail(pendingVerificationEmail);
      setNotice(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore invio link di verifica");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="auth-screen">
      <div className="auth-panel">
        <div className="auth-brand">
          <img src="/incassaflow-logo.svg" alt={APP_NAME} className="auth-logo" />
          <div>
            <h1>{APP_NAME}</h1>
            <p>{APP_TAGLINE}</p>
          </div>
        </div>

        <div className="segment-control">
          <button
            type="button"
            className={mode === "login" ? "active" : ""}
            onClick={() => setMode("login")}
          >
            Login
          </button>
          <button
            type="button"
            className={mode === "register" ? "active" : ""}
            onClick={() => setMode("register")}
          >
            Registrazione
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-grid">
          {mode === "register" && (
            <>
              {inviteToken && (
                <p style={{ background: "var(--surface-soft)", padding: "10px 14px", borderRadius: 8, marginBottom: 4, fontSize: "0.9rem" }}>
                  Sei stato invitato a usare IncassaFlow dal tuo commercialista.
                </p>
              )}
              {!inviteToken && (
                <div className="account-type-selector">
                  <button
                    type="button"
                    className={accountType === "business" ? "active" : ""}
                    onClick={() => setAccountType("business")}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                    Azienda
                  </button>
                  <button
                    type="button"
                    className={accountType === "consultant" ? "active" : ""}
                    onClick={() => setAccountType("consultant")}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    Commercialista
                  </button>
                </div>
              )}
              {isConsultantMode && (
                <p className="account-type-hint">
                  Potrai invitare i tuoi clienti e monitorare i loro crediti da un&apos;unica dashboard.
                </p>
              )}
              <label>
                Nome completo
                <input value={fullName} onChange={(event) => setFullName(event.target.value)} />
              </label>
              {!isConsultantMode && (
                <label>
                  Nome azienda
                  <input
                    value={workspaceName}
                    onChange={(event) => setWorkspaceName(event.target.value)}
                    required
                  />
                </label>
              )}
            </>
          )}

          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
            />
          </label>

          {mode === "login" && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label className="checkbox-row" style={{ margin: 0 }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                />
                Ricordami
              </label>
              <Link to="/reset-password" style={{ fontSize: "0.88rem" }}>
                Password dimenticata?
              </Link>
            </div>
          )}

          {mode === "register" && (
            <label className="consent-row">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                required
              />
              <span>
                Ho letto e accetto le{" "}
                <Link to="/terms" target="_blank" rel="noopener">Condizioni d&apos;uso</Link>
                {" "}e l&apos;
                <Link to="/privacy" target="_blank" rel="noopener">Informativa Privacy</Link>,
                inclusa la nomina di IncassaFlow a responsabile del trattamento ex art. 28 GDPR per i dati dei miei debitori.
              </span>
            </label>
          )}

          {error && <p className="error-text">{error}</p>}
          {notice && <p className="success-text">{notice}</p>}

          {pendingVerificationEmail && (
            <div className="guide-box">
              <p>
                Hai registrato <strong>{pendingVerificationEmail}</strong>. Prima del login devi confermare questa
                email.
              </p>
              <button type="button" className="ghost-btn" onClick={() => void handleResendVerification()} disabled={loading}>
                Invia di nuovo il link di verifica
              </button>
            </div>
          )}

          <button type="submit" className="primary-btn" disabled={loading}>
            {loading ? "Attendere..." : mode === "login" ? "Accedi" : "Crea account"}
          </button>
        </form>

        <div className="auth-legal-footer">
          <Link to="/privacy">Informativa Privacy</Link>
          <span>·</span>
          <Link to="/terms">Condizioni d&apos;uso</Link>
        </div>
      </div>
    </section>
  );
}
